// A drawn "title plate" for each video format: what that kind of video looks like, in one small scene (a painted
// landscape with a lower third for documentaries, a plotted curve for math, two hosts and a waveform for podcasts).
// Used on the type picker, the empty Home and the project header when there's no sample render yet. Every colour comes
// from the format's custom properties (styles-v3.css: .fart-<id>), so the brand tokens can replace them in one place.
// When the format has a sample (FormatInfo.sample), its poster shows instead, and the video plays muted on hover
// (fine pointers) or while it's on screen (touch), unless the user prefers reduced motion.
import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import type { FormatId, FormatSample } from "../formats";

const W = 320;
const H = 200;

/** Fake text: a rounded bar where a line of words would sit. */
const Bar = ({ x, y, w, h = 6, c = "var(--fa-ink)", o = 1 }: { x: number; y: number; w: number; h?: number; c?: string; o?: number }) => <rect x={x} y={y} width={w} height={h} rx={h / 2} fill={c} opacity={o} />;

function Documentary() {
  return (
    <>
      <circle cx="226" cy="74" r="30" fill="var(--fa-hi)" opacity=".85" />
      <circle cx="226" cy="74" r="58" fill="var(--fa-hi)" opacity=".1" />
      <path d="M0 128 C40 104 70 112 104 100 C140 88 170 110 206 102 C246 94 280 108 320 96 V200 H0Z" fill="var(--fa-mid)" opacity=".55" />
      <path d="M0 150 C50 132 92 146 140 134 C190 122 236 144 320 128 V200 H0Z" fill="var(--fa-mid)" opacity=".85" />
      <path d="M0 172 C70 160 130 172 200 162 C250 156 290 166 320 160 V200 H0Z" fill="var(--fa-bg2)" />
      {/* the lower third */}
      <rect x="22" y="146" width="3" height="28" fill="var(--fa-hi)" />
      <Bar x={32} y={149} w={96} h={8} />
      <Bar x={32} y={164} w={64} h={5} o={0.55} />
      {/* a source chip */}
      <rect x="226" y="166" width="72" height="16" rx="8" fill="none" stroke="var(--fa-ink)" strokeOpacity=".4" />
      <Bar x={236} y={172} w={52} h={4} o={0.5} />
    </>
  );
}

function Cartoon() {
  return (
    <>
      <path d="M0 160 C80 140 220 140 320 158 V200 H0Z" fill="var(--fa-mid)" />
      <circle cx="54" cy="44" r="14" fill="var(--fa-hi)" opacity=".9" />
      {/* the fox */}
      <path d="M86 98 L96 70 L110 92 Z M126 92 L140 70 L148 98 Z" fill="var(--fa-hi)" />
      <ellipse cx="117" cy="112" rx="34" ry="30" fill="var(--fa-hi)" />
      <path d="M100 118 Q117 138 134 118 Q117 126 100 118Z" fill="var(--fa-ink)" />
      <circle cx="106" cy="106" r="3.4" fill="var(--fa-bg2)" />
      <circle cx="128" cy="106" r="3.4" fill="var(--fa-bg2)" />
      <path d="M86 140 Q117 176 150 140 L156 166 Q117 182 80 166Z" fill="var(--fa-hi)" opacity=".85" />
      {/* the owl */}
      <ellipse cx="222" cy="122" rx="30" ry="38" fill="var(--fa-ink)" opacity=".92" />
      <circle cx="210" cy="110" r="10" fill="var(--fa-bg1)" />
      <circle cx="234" cy="110" r="10" fill="var(--fa-bg1)" />
      <circle cx="211" cy="111" r="4" fill="var(--fa-ink)" />
      <circle cx="233" cy="111" r="4" fill="var(--fa-ink)" />
      <path d="M218 122 L222 130 L226 122Z" fill="var(--fa-hi)" />
      {/* a speech bubble */}
      <path d="M176 30 h94 a14 14 0 0 1 14 14 v18 a14 14 0 0 1 -14 14 h-58 l-14 12 v-12 h-22 a14 14 0 0 1 -14 -14 v-18 a14 14 0 0 1 14 -14z" fill="var(--fa-ink)" />
      <circle cx="204" cy="53" r="4" fill="var(--fa-bg2)" />
      <circle cx="222" cy="53" r="4" fill="var(--fa-bg2)" />
      <circle cx="240" cy="53" r="4" fill="var(--fa-bg2)" />
    </>
  );
}

function ShortFilm() {
  return (
    <>
      <path d="M150 38 L106 172 H214 L170 38Z" fill="var(--fa-hi)" opacity=".16" />
      <rect x="156" y="30" width="8" height="12" rx="2" fill="var(--fa-hi)" />
      <ellipse cx="160" cy="172" rx="70" ry="8" fill="var(--fa-hi)" opacity=".2" />
      {/* a figure in the light */}
      <circle cx="160" cy="118" r="9" fill="var(--fa-bg2)" />
      <path d="M146 172 L150 134 Q160 126 170 134 L174 172Z" fill="var(--fa-bg2)" />
      <path d="M0 150 L60 120 L90 136 L130 112 V172 H0Z M190 112 L236 128 L270 106 L320 132 V172 H190Z" fill="var(--fa-mid)" opacity=".7" />
      {/* the title, spaced wide */}
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <Bar key={i} x={56 + i * 36} y={70} w={22} h={5} o={0.75} />
      ))}
      {/* 2.39 letterbox */}
      <rect width={W} height="26" fill="var(--fa-bar)" />
      <rect y="174" width={W} height="26" fill="var(--fa-bar)" />
    </>
  );
}

function MathArt() {
  const curve = Array.from({ length: 41 }, (_, i) => {
    const x = -2 + i * 0.1;
    return `${i ? "L" : "M"}${(40 + (x + 2) * 60).toFixed(1)} ${(170 - x * x * 30).toFixed(1)}`;
  }).join(" ");
  return (
    <>
      <g stroke="var(--fa-ink)" strokeOpacity=".07">
        {Array.from({ length: 16 }, (_, i) => (
          <line key={`v${i}`} x1={i * 20} y1="0" x2={i * 20} y2={H} />
        ))}
        {Array.from({ length: 10 }, (_, i) => (
          <line key={`h${i}`} x1="0" y1={i * 20} x2={W} y2={i * 20} />
        ))}
      </g>
      <line x1="30" y1="170" x2="290" y2="170" stroke="var(--fa-ink)" strokeOpacity=".5" strokeWidth="1.4" />
      <line x1="160" y1="40" x2="160" y2="184" stroke="var(--fa-ink)" strokeOpacity=".5" strokeWidth="1.4" />
      <path d={curve} fill="none" stroke="var(--fa-hi)" strokeWidth="3" strokeLinecap="round" />
      {/* the tangent at x = 1 */}
      <line x1="160" y1="200" x2="280" y2="80" stroke="var(--fa-mid)" strokeWidth="2" strokeDasharray="5 5" />
      <circle cx="220" cy="140" r="5" fill="var(--fa-ink)" />
      <text x="34" y="46" className="fart-math">
        f′(x) = 2x
      </text>
    </>
  );
}

function Explainer() {
  return (
    <>
      <path d="M0 170 H320 V200 H0Z" fill="var(--fa-mid)" opacity=".6" />
      {/* the host */}
      <circle cx="78" cy="88" r="24" fill="var(--fa-hi)" />
      <rect x="64" y="72" width="28" height="6" rx="3" fill="var(--fa-bg2)" opacity=".9" />
      <path d="M44 170 Q46 120 78 118 Q110 120 112 170Z" fill="var(--fa-ink)" />
      <path d="M104 134 L146 104" stroke="var(--fa-ink)" strokeWidth="10" strokeLinecap="round" />
      {/* the diagram card */}
      <rect x="152" y="36" width="146" height="112" rx="12" fill="var(--fa-ink)" opacity=".95" />
      <rect x="170" y="100" width="20" height="30" rx="3" fill="var(--fa-mid)" />
      <rect x="198" y="84" width="20" height="46" rx="3" fill="var(--fa-mid)" />
      <rect x="226" y="66" width="20" height="64" rx="3" fill="var(--fa-hi)" />
      <rect x="254" y="76" width="20" height="54" rx="3" fill="var(--fa-mid)" />
      <Bar x={170} y={50} w={72} h={6} c="var(--fa-bg2)" o={0.7} />
    </>
  );
}

function Short() {
  return (
    <>
      <rect x="118" y="14" width="84" height="172" rx="16" fill="var(--fa-bg2)" stroke="var(--fa-ink)" strokeOpacity=".35" strokeWidth="2" />
      <rect x="124" y="20" width="72" height="160" rx="11" fill="var(--fa-mid)" opacity=".55" />
      <circle cx="160" cy="70" r="22" fill="var(--fa-hi)" opacity=".35" />
      <text x="160" y="120" textAnchor="middle" className="fart-caption">
        WAIT
      </text>
      <text x="160" y="146" textAnchor="middle" className="fart-caption fart-caption-hi">
        WHAT?
      </text>
      <rect x="132" y="166" width="56" height="3" rx="1.5" fill="var(--fa-ink)" opacity=".3" />
      <rect x="132" y="166" width="24" height="3" rx="1.5" fill="var(--fa-hi)" />
      {/* motion lines either side */}
      <path d="M86 70 h18 M78 92 h26 M92 114 h12 M216 80 h18 M216 102 h26 M216 124 h12" stroke="var(--fa-ink)" strokeOpacity=".35" strokeWidth="3" strokeLinecap="round" />
    </>
  );
}

function MusicVideo() {
  const bars = [18, 34, 52, 30, 64, 44, 80, 58, 40, 70, 48, 26, 56, 36, 20];
  return (
    <>
      <circle cx="160" cy="70" r="40" fill="var(--fa-hi)" opacity=".2" />
      <circle cx="160" cy="70" r="26" fill="var(--fa-hi)" opacity=".8" />
      <path d="M-10 120 L330 40" stroke="var(--fa-ink)" strokeOpacity=".12" strokeWidth="18" />
      <path d="M-10 150 L330 80" stroke="var(--fa-mid)" strokeOpacity=".4" strokeWidth="6" />
      {bars.map((h, i) => (
        <rect key={i} x={28 + i * 18} y={180 - h} width="10" height={h} rx="5" fill={i === 6 || i === 9 ? "var(--fa-hi)" : "var(--fa-ink)"} opacity={i === 6 || i === 9 ? 1 : 0.6} />
      ))}
    </>
  );
}

function Slideshow() {
  const Photo = ({ x, y, r, sky, sun }: { x: number; y: number; r: number; sky: string; sun?: boolean }) => (
    <g transform={`rotate(${r} ${x + 60} ${y + 48})`}>
      <rect x={x} y={y} width="120" height="96" rx="4" fill="var(--fa-ink)" />
      <rect x={x + 7} y={y + 7} width="106" height="70" rx="2" fill={sky} />
      {sun && <circle cx={x + 82} cy={y + 30} r="10" fill="var(--fa-hi)" />}
      <path d={`M${x + 7} ${y + 77} L${x + 40} ${y + 50} L${x + 62} ${y + 66} L${x + 82} ${y + 54} L${x + 113} ${y + 77}Z`} fill="var(--fa-bg2)" opacity=".7" />
    </g>
  );
  return (
    <>
      <Photo x={48} y={58} r={-9} sky="var(--fa-mid)" />
      <Photo x={156} y={50} r={8} sky="var(--fa-mid)" />
      <Photo x={100} y={42} r={-1} sky="var(--fa-soft)" sun />
    </>
  );
}

function Kinetic() {
  return (
    <>
      <text x="28" y="78" className="fart-type">
        SAY IT
      </text>
      <text x="28" y="134" className="fart-type fart-type-hi">
        LOUDER
      </text>
      <text x="30" y="172" className="fart-type fart-type-small">
        and mean it.
      </text>
      <path d="M262 46 l20 -8 M268 66 l24 0 M262 86 l20 8" stroke="var(--fa-hi)" strokeWidth="4" strokeLinecap="round" />
    </>
  );
}

function Podcast() {
  const wave = [6, 14, 24, 10, 30, 18, 36, 22, 12, 28, 16, 8, 20, 32, 14, 24, 10, 6];
  return (
    <>
      <circle cx="84" cy="80" r="36" fill="var(--fa-mid)" />
      <circle cx="84" cy="80" r="42" fill="none" stroke="var(--fa-hi)" strokeWidth="3" />
      <circle cx="84" cy="72" r="12" fill="var(--fa-ink)" opacity=".9" />
      <path d="M62 106 Q84 84 106 106" fill="var(--fa-ink)" opacity=".9" />
      <circle cx="236" cy="80" r="36" fill="var(--fa-mid)" opacity=".75" />
      <circle cx="236" cy="72" r="12" fill="var(--fa-ink)" opacity=".7" />
      <path d="M214 106 Q236 84 258 106" fill="var(--fa-ink)" opacity=".7" />
      {wave.map((h, i) => (
        <rect key={i} x={70 + i * 10} y={160 - h / 2} width="5" height={h} rx="2.5" fill={i < 8 ? "var(--fa-hi)" : "var(--fa-ink)"} opacity={i < 8 ? 1 : 0.45} />
      ))}
    </>
  );
}

function Demo() {
  return (
    <>
      <rect x="34" y="26" width="252" height="156" rx="10" fill="var(--fa-ink)" opacity=".95" />
      <rect x="34" y="26" width="252" height="22" rx="10" fill="var(--fa-mid)" />
      <rect x="34" y="40" width="252" height="8" fill="var(--fa-mid)" />
      {[0, 1, 2].map((i) => (
        <circle key={i} cx={48 + i * 12} cy="37" r="3.5" fill="var(--fa-bg1)" opacity=".6" />
      ))}
      <rect x="48" y="60" width="60" height="108" rx="6" fill="var(--fa-soft)" opacity=".5" />
      <Bar x={120} y={62} w={110} h={8} c="var(--fa-bg2)" o={0.5} />
      <Bar x={120} y={78} w={80} h={6} c="var(--fa-bg2)" o={0.3} />
      <rect x="120" y="96" width="70" height="56" rx="6" fill="var(--fa-soft)" opacity=".5" />
      <rect x="198" y="96" width="74" height="56" rx="6" fill="var(--fa-soft)" opacity=".5" />
      {/* the callout */}
      <rect x="192" y="90" width="86" height="68" rx="9" fill="none" stroke="var(--fa-hi)" strokeWidth="3" />
      <rect x="208" y="164" width="70" height="20" rx="10" fill="var(--fa-hi)" />
      <Bar x={218} y={171} w={50} h={5} c="var(--fa-bg2)" o={0.8} />
    </>
  );
}

function Ad() {
  return (
    <>
      <ellipse cx="160" cy="150" rx="96" ry="18" fill="var(--fa-mid)" />
      <circle cx="160" cy="96" r="70" fill="var(--fa-hi)" opacity=".12" />
      <rect x="132" y="52" width="56" height="96" rx="12" fill="var(--fa-ink)" />
      <rect x="146" y="38" width="28" height="18" rx="4" fill="var(--fa-mid)" />
      <rect x="132" y="84" width="56" height="34" fill="var(--fa-hi)" />
      <Bar x={144} y={98} w={32} h={6} c="var(--fa-bg2)" o={0.8} />
      <rect x="110" y="170" width="100" height="20" rx="10" fill="var(--fa-hi)" />
      <Bar x={128} y={177} w={64} h={6} c="var(--fa-bg2)" o={0.85} />
    </>
  );
}

const ART: Record<FormatId, () => ReactNode> = {
  ad: Ad,
  documentary: Documentary,
  cartoon: Cartoon,
  "short-film": ShortFilm,
  "math-explainer": MathArt,
  explainer: Explainer,
  short: Short,
  "music-video": MusicVideo,
  slideshow: Slideshow,
  kinetic: Kinetic,
  podcast: Podcast,
  demo: Demo,
};

/** The drawn plate for a format (decorative: the card names the format in text). */
export function FormatArt({ format, className = "" }: { format: FormatId; className?: string }) {
  const Art = ART[format] ?? Ad;
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, "");
  return (
    <svg className={`fart fart-${format} ${className}`} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid slice" aria-hidden focusable="false">
      <defs>
        <linearGradient id={`fa-g-${uid}`} x1="0" y1="0" x2="0.35" y2="1">
          <stop offset="0" stopColor="var(--fa-bg1)" />
          <stop offset="1" stopColor="var(--fa-bg2)" />
        </linearGradient>
        <radialGradient id={`fa-v-${uid}`} cx=".5" cy=".45" r=".75">
          <stop offset=".6" stopColor="var(--fa-bar)" stopOpacity="0" />
          <stop offset="1" stopColor="var(--fa-bar)" stopOpacity=".55" />
        </radialGradient>
      </defs>
      <rect width={W} height={H} fill={`url(#fa-g-${uid})`} />
      <Art />
      <rect width={W} height={H} fill={`url(#fa-v-${uid})`} />
    </svg>
  );
}

const reducedMotion = () => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
const finePointer = () => window.matchMedia?.("(hover: hover) and (pointer: fine)").matches ?? false;

/**
 * The card's picture: the sample render (poster, video on hover or while visible) or the drawn plate. `active` lets
 * the parent card drive playback from its own hover/focus.
 */
export function FormatCover({ format, sample, active = false }: { format: FormatId; sample?: FormatSample | null; active?: boolean }) {
  const video = useRef<HTMLVideoElement>(null);
  const box = useRef<HTMLSpanElement>(null);
  const [seen, setSeen] = useState(false);
  const [failed, setFailed] = useState(false);
  const hasVideo = !!sample?.videoUrl && !failed;

  // touch screens: play while at least 60% on screen
  useEffect(() => {
    if (!hasVideo || finePointer() || reducedMotion() || !box.current || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(([e]) => setSeen(!!e && e.intersectionRatio >= 0.6), { threshold: [0, 0.6, 1] });
    io.observe(box.current);
    return () => io.disconnect();
  }, [hasVideo]);

  const play = hasVideo && !reducedMotion() && (active || seen);
  useEffect(() => {
    const v = video.current;
    if (!v) return;
    if (play) void v.play().catch(() => undefined);
    else {
      v.pause();
      v.currentTime = 0;
    }
  }, [play]);

  return (
    <span className="fcover" ref={box}>
      {sample?.posterUrl ? <img src={sample.posterUrl} alt="" loading="lazy" className="fcover-poster" /> : <FormatArt format={format} />}
      {hasVideo && <video ref={video} src={sample!.previewUrl || sample!.videoUrl} poster={sample!.posterUrl || undefined} muted loop playsInline preload="none" className={`fcover-video${play ? " is-on" : ""}`} onError={() => setFailed(true)} aria-hidden />}
    </span>
  );
}
